import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Segmente, libelleType, dateCourte, messageErreur } from './commun';

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

export default function TableauDeBord({ referentiel }) {
  const [societe, setSociete] = useState('');
  const [periode, setPeriode] = useState('3m');
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    try { setD((await api.get('/commercial/tableau', { params: { periode, societe: societe || undefined } })).data); setErreur(''); }
    catch (e) { setErreur(messageErreur(e, 'Impossible de charger le tableau de bord.')); }
  }, [periode, societe]);
  useEffect(() => { charger(); }, [charger]);

  const multi = (referentiel?.societes.length || 0) > 1;
  const taux = d && d.travailles ? `${pct(d.signes, d.travailles)} %` : '—';
  const maxType = d ? Math.max(1, ...d.par_type.map((t) => t.n)) : 1;

  return (
    <div className="tableau-bord">
      <div className="page-entete">
        <h1>Tableau de bord commercial</h1>
        <div className="entete-actions">
          {multi && (
            <Segmente etiquette="Société" valeur={societe} onChange={setSociete}
              options={[{ cle: '', libelle: 'Les deux' }].concat(referentiel.societes.map((s) => ({ cle: String(s.id), libelle: s.nom })))} />
          )}
          <Segmente etiquette="Période" valeur={periode} onChange={setPeriode}
            options={[{ cle: '30j', libelle: '30 jours' }, { cle: '3m', libelle: '3 mois' }, { cle: 'annee', libelle: 'Depuis janvier' }]} />
        </div>
      </div>
      {d && <p className="sous-titre">Du {dateCourte(d.debut)} au {dateCourte(d.fin)}</p>}
      {erreur && <div className="alerte">{erreur}</div>}
      {!d && !erreur && <div className="attente">Chargement…</div>}

      {d && (
        <>
          <div className="kpis">
            <div className="carte kpi"><span className="kpi-titre">Stock de prospects</span><span className="kpi-valeur">{d.stock}</span>
              <span className="kpi-aide">Comptes au statut Prospect ou En discussion, aujourd'hui.</span></div>
            <div className="carte kpi"><span className="kpi-titre">Prospects travaillés</span><span className="kpi-valeur">{d.travailles}</span>
              <span className="kpi-aide">Comptes avec au moins un échange noté sur la période, hors clients déjà acquis avant.</span></div>
            <div className="carte kpi"><span className="kpi-titre">Clients signés</span><span className="kpi-valeur vert">{d.signes}</span>
              <span className="kpi-aide">Passés au statut Client dans l'outil sur la période. Les clients importés n'y comptent pas.</span></div>
            <div className="carte kpi"><span className="kpi-titre">Taux de transformation</span><span className="kpi-valeur bleu">{taux}</span>
              <span className="kpi-aide">Clients signés ÷ prospects travaillés sur la même période.</span></div>
          </div>

          {(d.sans_relance > 0 || d.en_retard > 0) && (
            <div className="bandeau-info">
              {d.en_retard > 0 && <><strong>{d.en_retard} relance{d.en_retard > 1 ? 's' : ''} en retard.</strong> </>}
              {d.sans_relance > 0 && <><a href="#/comptes?a_planifier=1"><strong>{d.sans_relance} prospect{d.sans_relance > 1 ? 's' : ''} sans relance prévue</strong></a> : sans date, personne ne les rappellera.</>}
            </div>
          )}

          <div className="tb-grille">
            <div className="tb-colonne">
              <div className="carte">
                <h2>Entonnoir sur la période</h2>
                {[['Prospects travaillés', d.travailles, 'barre-1'], ['Passés en discussion ou signés', d.discussion, 'barre-2'], ['Clients signés', d.signes, 'barre-3']].map(([l, n, cl]) => (
                  <div key={l} className="barre-ligne">
                    <span>{l}</span>
                    <div className="barre-fond"><div className={`barre ${cl}`} style={{ width: `${Math.min(100, pct(n, Math.max(d.travailles, d.discussion, d.signes)))}%` }} /></div>
                    <span className="nombre">{n}</span>
                  </div>
                ))}
              </div>
              <div className="carte">
                <h2>Stock de prospects par type de client</h2>
                {d.par_type.length === 0 && <p className="discret">Aucun prospect.</p>}
                {d.par_type.map((t) => (
                  <div key={t.type} className="barre-ligne">
                    <span>{libelleType(t.type)}</span>
                    <div className="barre-fond fine"><div className="barre barre-gris" style={{ width: `${pct(t.n, maxType)}%` }} /></div>
                    <span className="nombre">{t.n}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="tb-colonne">
              <div className="carte">
                <h2>Clients signés sur la période · {d.signes_liste.length}</h2>
                {d.signes_liste.length === 0 && <p className="discret">Aucun client signé sur la période.</p>}
                {d.signes_liste.map((c) => (
                  <a key={c.id} href={`#/compte/${c.id}`} className="tb-signe">
                    <strong>{c.nom}</strong>
                    <span>{libelleType(c.type)}{c.ville ? ` · ${c.ville}` : ''}</span>
                    <span>{c.commercial_nom || '—'}{multi && !societe ? ` · ${c.societe_nom}` : ''}</span>
                    <span className="droite nombre-leger">{dateCourte(c.signe_le)}</span>
                  </a>
                ))}
              </div>
              <div className="carte">
                <h2>Par commercial</h2>
                <div className="tableau sans-bord">
                  <table>
                    <thead><tr><th>Commercial</th><th className="droite">Stock</th><th className="droite">Travaillés</th><th className="droite">Signés</th><th className="droite">Taux</th><th className="droite">En retard</th></tr></thead>
                    <tbody>
                      {d.par_commercial.map((v) => (
                        <tr key={v.id || 'aucun'}>
                          <td><strong>{v.nom}</strong>{multi && !societe && v.societes ? <span className="discret"> · {v.societes}</span> : ''}</td>
                          <td className="droite nombre">{v.stock}</td>
                          <td className="droite nombre">{v.travailles}</td>
                          <td className="droite nombre vert">{v.signes}</td>
                          <td className="droite nombre">{v.travailles ? `${pct(v.signes, v.travailles)} %` : '—'}</td>
                          <td className={`droite nombre ${v.en_retard ? 'rouge' : ''}`}>{v.en_retard}</td>
                        </tr>
                      ))}
                      {d.par_commercial.length === 0 && <tr><td colSpan={6} className="discret">Aucun compte.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
